import React, { FC, useCallback } from 'react';

import { SafeAreaView, StyleSheet, View } from 'react-native';

import ActionButton, { ActionButtonSizes, ActionButtonVariants } from 'components/action-button';
import { CenteredRowContainer } from 'components/layout/centered-row-container';
import LoadingAnimation from 'components/loading-animation';
import { Table } from 'components/table';
import { t } from 'state/intl/translations';
import { spacing } from 'styles/constants';
import { isNative } from 'utils/environment';
import { ITable } from 'utils/table';

import { TransactionTable, TransactionTableRow } from './types';

interface HistoryLazyTableListProps {
  tables: TransactionTable[];
  visibleTransactions?: number;
  handleOnPressRow: (row: TransactionTableRow | Partial<TransactionTableRow>) => void;
  loadingMore?: boolean;
  onLoadMore?: () => void;
}

export const HistoryLazyTableList: FC<HistoryLazyTableListProps> = ({
  tables,
  visibleTransactions,
  handleOnPressRow,
  loadingMore,
  onLoadMore,
}) => {
  const renderItem = useCallback(
    ({ item, index }: { item: ITable<Partial<TransactionTableRow>>; index: number }) => (
      <Table<TransactionTableRow>
        key={`historyTable.${index}`}
        table={item}
        onPressRow={handleOnPressRow}
        marginTop="$4"
      />
    ),
    [handleOnPressRow]
  );

  const visibleTables = React.useMemo(() => {
    if (isNative && visibleTransactions) {
      return tables.slice(0, visibleTransactions);
    }
    return tables;
  }, [tables, visibleTransactions]);

  // Lazy Loading only for Native...
  if (isNative && visibleTransactions) {
    return (
      <SafeAreaView style={styles.container}>
        {visibleTables.map((item, index) => renderItem({ item, index }))}
        {loadingMore && (
          <CenteredRowContainer>
            <View style={{ padding: spacing.$8 }}>
              <LoadingAnimation />
            </View>
          </CenteredRowContainer>
        )}
        {isNative && !loadingMore && visibleTransactions < tables.length && (
          <View style={styles.loadMoreContainer}>
            <ActionButton
              variant={ActionButtonVariants.OUTLINE_V2}
              onPress={onLoadMore}
              size={ActionButtonSizes.SMALL}
              alignSelf="center"
            >
              {t('loadMore')}
            </ActionButton>
          </View>
        )}
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      {visibleTables.map((item, index) => renderItem({ item, index }))}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadMoreContainer: {
    padding: spacing.$4,
  },
});
